import { useState, useEffect, useCallback, useRef } from "react";

export type FormErrors<T> = Partial<Record<keyof T & string, string>>;

interface UseModalFormOptions<T> {
  /** Recomputed whenever the modal opens, so edit mode seeds from the record
   * and create mode from blanks. Previously every modal wrote its initial
   * object out twice - once as the useState seed, once in the reset effect. */
  initialValues: T;
  isOpen: boolean;
  /** Return a message per invalid field; an empty object means valid. */
  validate?: (values: T) => FormErrors<T>;
  onSubmit: (values: T) => Promise<void> | void;
}

/**
 * Form state for a modal: values, per-field errors, and the in-flight flag.
 *
 * Seven modals each carried their own copy of this - the same `errors`
 * record, `isSubmitting` boolean, reset-on-open effect, validate/guard/submit
 * sequence, and a handleInputChange that clears a field's error as the user
 * types.
 */
export const useModalForm = <T extends Record<string, any>>({
  initialValues,
  isOpen,
  validate,
  onSubmit,
}: UseModalFormOptions<T>) => {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<FormErrors<T>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Read through a ref so the reset effect depends only on `isOpen`. Callers
  // build initialValues inline, so it is a new object every render and would
  // otherwise reset the form on every keystroke.
  const initialRef = useRef(initialValues);
  initialRef.current = initialValues;

  useEffect(() => {
    if (!isOpen) return;
    setValues(initialRef.current);
    setErrors({});
    setIsSubmitting(false);
  }, [isOpen]);

  const setValue = useCallback(
    <K extends keyof T & string>(field: K, value: T[K]) => {
      setValues((prev) => ({ ...prev, [field]: value }));
      // Clear this field's error as the user corrects it.
      setErrors((prev) =>
        prev[field] ? { ...prev, [field]: undefined } : prev
      );
    },
    []
  );

  /** Merge several fields at once - for dependent fields that change together. */
  const patch = useCallback((partial: Partial<T>) => {
    setValues((prev) => ({ ...prev, ...partial }));
  }, []);

  const handleSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault();

      const nextErrors = validate ? validate(values) : {};
      const invalid = Object.values(nextErrors).some(Boolean);
      setErrors(nextErrors);
      if (invalid) return;

      setIsSubmitting(true);
      try {
        await onSubmit(values);
      } finally {
        setIsSubmitting(false);
      }
    },
    [validate, values, onSubmit]
  );

  return {
    values,
    setValues,
    setValue,
    patch,
    errors,
    setErrors,
    isSubmitting,
    handleSubmit,
  };
};
